import React, { useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { BarChart } from 'react-native-gifted-charts';
import { useTheme } from '../../theme';
import {
  axisUnit,
  borderWidth,
  compactAxisTick,
  fs,
  s,
  v,
  formatMoney,
} from '../../utils/libs';
import ChartTooltip from './ChartTooltip';
import PositionedChartTooltip from './PositionedChartTooltip';
import { getBarChartPointX } from './chartTooltipPosition';

type DataPoint = {
  moneyIn?: number;
  revenue?: number;
  time?: string;
  label?: string;
  bucket?: string;
};

type Props = {
  data: DataPoint[];
  color?: string;
  valueLabel?: string;
};

type ChartPoint = {
  value: number;
  label: string;
  fullLabel: string;
  frontColor: string;
};

/** Fixed box for a custom-row label — independent of bar spacing. */
const ROW_LABEL_W = 56;
/** Min px between custom ticks. */
const ROW_MIN_GAP_PX = 64;

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Backend bucket keys. */
const HOUR_BUCKET_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2})$/;
const DAY_BUCKET_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_BUCKET_RE = /^(\d{4})-(\d{2})$/;

const parseHourBucket = (
  bucket?: string,
): { hour24: number; hour12: number; suffix: 'AM' | 'PM' } | null => {
  const match = String(bucket || '').match(HOUR_BUCKET_RE);
  if (!match) return null;
  const hour24 = Number(match[4]);
  if (Number.isNaN(hour24) || hour24 < 0 || hour24 > 23) return null;
  return {
    hour24,
    hour12: hour24 % 12 || 12,
    suffix: hour24 >= 12 ? 'PM' : 'AM',
  };
};

/** Row tick for an hour bucket, e.g. "5 PM". */
const formatHourRowLabel = (bucket?: string): string => {
  const parsed = parseHourBucket(bucket);
  if (!parsed) return '';
  return `${parsed.hour12} ${parsed.suffix}`;
};

/** Tooltip header for an hour bucket, e.g. "5:00 PM". */
const formatHourTooltipLabel = (bucket?: string): string => {
  const parsed = parseHourBucket(bucket);
  if (!parsed) return '';
  return `${parsed.hour12}:00 ${parsed.suffix}`;
};

/** Day bucket YYYY-MM-DD → "Jul 15". */
const formatDayFromBucket = (bucket: string): string => {
  const m = bucket.match(DAY_BUCKET_RE);
  if (!m) return '';
  return `${MONTHS[Number(m[2]) - 1] || ''} ${Number(m[3])}`.trim();
};

/** Month bucket YYYY-MM → "Jul" (year is redundant across one year). */
const formatMonthFromBucket = (bucket: string): string => {
  const m = bucket.match(MONTH_BUCKET_RE);
  if (!m) return '';
  return MONTHS[Number(m[2]) - 1] || '';
};

/** Axis (row) label for any bucket shape. */
const formatAxisLabel = (p: DataPoint): string => {
  const hour = formatHourRowLabel(p.bucket);
  if (hour) return hour;

  const bucket = String(p.bucket || '');
  const serverLabel = String(p.label || '').trim();

  if (DAY_BUCKET_RE.test(bucket)) {
    return serverLabel || formatDayFromBucket(bucket);
  }
  if (MONTH_BUCKET_RE.test(bucket)) {
    // Ignore server label here — it carries the year we don't want on the axis.
    return formatMonthFromBucket(bucket) || serverLabel;
  }
  if (serverLabel) return serverLabel;
  if (p.time) {
    return new Date(p.time).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
  return '';
};

const resolveTooltipLabel = (p: DataPoint): string => {
  const hour = formatHourTooltipLabel(p.bucket);
  if (hour) return hour;
  return formatAxisLabel(p);
};

/** Nice step >= raw, biased to human-friendly multiples. */
const niceStep = (raw: number): number => {
  const safe = Math.max(1, raw);
  const candidates = [1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 14, 15, 20, 24, 30];
  return candidates.find(c => c >= safe) ?? safe;
};

const PaymentsBarChart: React.FC<Props> = ({
  data,
  color,
  valueLabel = 'Money In',
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [containerW, setContainerW] = useState<number | null>(null);
  const yAxisLabelW = s(Platform.OS === 'web' ? 56 : 40);
  const chartWidth = useMemo(() => {
    if (!containerW) return 0;
    const measured = containerW - yAxisLabelW - s(16);
    return Math.max(measured, s(120));
  }, [containerW, yAxisLabelW]);

  const maxY = useMemo(() => {
    const max = Math.max(
      0,
      ...(data || []).map(d => Number(d.revenue ?? d.moneyIn ?? 0)),
    );
    const rounded = Math.ceil(max / 10) * 10;
    return rounded === 0 ? 10 : rounded;
  }, [data]);

  const yUnit = useMemo(() => axisUnit(maxY), [maxY]);
  const barColor = color || theme.colors.success;

  const barData = useMemo((): ChartPoint[] => {
    const points = data || [];
    return points.map(p => ({
      value: Number(p.revenue ?? p.moneyIn ?? 0),
      // Native labels always clip to bar spacing — blank them for every
      // bar and render the custom absolute row below instead.
      label: '',
      fullLabel: resolveTooltipLabel(p),
      frontColor: barColor,
    }));
  }, [barColor, data]);

  const initialSpacing = s(10);
  const endSpacing = s(24);
  const pointerLabelW = s(150);

  /** Fill the plot width: each bar footprint = barWidth + spacing. */
  const { barWidth, spacing } = useMemo(() => {
    const n = Math.max(barData.length, 1);
    if (!chartWidth) return { barWidth: s(10), spacing: s(6) };
    const available = Math.max(chartWidth - initialSpacing - endSpacing, s(40));
    const unit = available / n;
    const gap = Math.max(s(1), Math.min(unit * 0.4, s(16)));
    const width = Math.max(s(2), unit - gap);
    return { barWidth: width, spacing: gap };
  }, [barData.length, chartWidth, endSpacing, initialSpacing]);

  /** Pixel-based tick selection — matches bar centers. */
  const axisTicks = useMemo(() => {
    const points = data || [];
    const n = points.length;
    if (!chartWidth || n === 0) return [];

    const labelW = s(ROW_LABEL_W);
    const minGap = s(ROW_MIN_GAP_PX);
    const maxTicks = Math.max(2, Math.floor(chartWidth / minGap));
    const step = niceStep(Math.ceil((n - 1) / Math.max(maxTicks - 1, 1)));

    const out: { index: number; x: number; text: string; labelW: number }[] =
      [];
    for (let i = 0; i < n; i += step) {
      const text = formatAxisLabel(points[i]);
      if (!text) continue;
      out.push({
        index: i,
        x: getBarChartPointX({ index: i, initialSpacing, barWidth, spacing }),
        text,
        labelW,
      });
    }

    // Always try to keep the last bar without overlapping its neighbour.
    const lastIdx = n - 1;
    if (lastIdx > 0 && out[out.length - 1]?.index !== lastIdx) {
      const text = formatAxisLabel(points[lastIdx]);
      if (text) {
        const lastX = getBarChartPointX({
          index: lastIdx,
          initialSpacing,
          barWidth,
          spacing,
        });
        const prev = out[out.length - 1];
        if (!prev || lastX - prev.x >= minGap * 0.75) {
          out.push({ index: lastIdx, x: lastX, text, labelW });
        } else {
          out[out.length - 1] = { index: lastIdx, x: lastX, text, labelW };
        }
      }
    }
    return out;
  }, [barWidth, chartWidth, data, initialSpacing, spacing]);

  // gifted-charts BarChart resets pointerIndex to -1 on touch release while
  // keeping pointerX (and thus the tooltip) alive for pointerVanishDelay ms.
  // Freeze the last valid index so the tooltip holds its bar/content/position
  // until it actually vanishes, instead of snapping to index 0.
  const lastPointerIndexRef = useRef(0);

  const isEmpty = (barData?.length || 0) === 0;
  const chartKey = useMemo(
    () => `${chartWidth}:${barData.length}:${maxY}:${valueLabel}`,
    [chartWidth, barData.length, maxY, valueLabel],
  );

  return (
    <View
      style={styles.container}
      onLayout={e => setContainerW(e.nativeEvent.layout.width)}>
      {containerW ? (
        <>
          <View style={{ overflow: 'hidden' }}>
            <BarChart
              key={chartKey}
              data={barData}
              width={chartWidth}
              xAxisLength={chartWidth}
              rulesLength={chartWidth}
              endSpacing={endSpacing}
              spacing={spacing}
              initialSpacing={initialSpacing}
              barWidth={barWidth}
              disableScroll
              yAxisLabelWidth={yAxisLabelW}
              noOfSections={5}
              maxValue={maxY}
              frontColor={barColor}
              yAxisTextStyle={{
                color: theme.colors.text,
                fontSize: fs(11),
              }}
              height={v(Platform.OS === 'web' ? 300 : 200)}
              xAxisLabelsHeight={0}
              isAnimated
              xAxisTextNumberOfLines={1}
              showFractionalValues={false}
              formatYLabel={label =>
                compactAxisTick(Math.round(Number(label || '0')), yUnit)
              }
              pointerConfig={
                isEmpty
                  ? undefined
                  : {
                      pointerColor: barColor,
                      pointerStripColor: theme.colors.border,
                      pointerStripWidth: borderWidth(
                        Platform.OS === 'web' ? 0.5 : 1,
                      ),
                      pointerLabelWidth: pointerLabelW,
                      pointerLabelHeight: v(48),
                      autoAdjustPointerLabelPosition: true,
                      activatePointersInstantlyOnTouch: true,
                      stripOverPointer: true,
                      pointerVanishDelay: 5000,
                      pointerLabelComponent: (
                        _items: any[],
                        _secondary: any[],
                        index: number,
                      ) => {
                        if (index >= 0) lastPointerIndexRef.current = index;
                        const activeIndex =
                          index >= 0 ? index : lastPointerIndexRef.current;
                        const safeIndex = Math.max(
                          0,
                          Math.min(activeIndex, barData.length - 1),
                        );
                        const item = barData[safeIndex];
                        const pointX = getBarChartPointX({
                          index: safeIndex,
                          initialSpacing,
                          barWidth,
                          spacing,
                        });
                        return (
                          <PositionedChartTooltip
                            pointX={pointX}
                            tooltipWidth={pointerLabelW}
                            boundaryWidth={chartWidth}>
                            <ChartTooltip
                              header={String(
                                item?.fullLabel || item?.label || '',
                              )}
                              items={[
                                {
                                  color: barColor,
                                  label: valueLabel,
                                  value: Number(item?.value ?? 0),
                                  formatter: n =>
                                    formatMoney(n),
                                },
                              ]}
                            />
                          </PositionedChartTooltip>
                        );
                      },
                    }
              }
            />
          </View>
          {axisTicks.length > 0 ? (
            <View
              pointerEvents="none"
              style={{
                height: v(20),
                marginLeft: yAxisLabelW,
                width: chartWidth,
                position: 'relative',
                marginTop: v(2),
              }}>
              {axisTicks.map(t => (
                <Text
                  key={t.index}
                  numberOfLines={1}
                  style={{
                    position: 'absolute',
                    left: t.x - t.labelW / 2,
                    width: t.labelW,
                    textAlign: 'center',
                    color: theme.colors.text,
                    fontSize: fs(10),
                  }}>
                  {t.text}
                </Text>
              ))}
            </View>
          ) : null}
        </>
      ) : (
        <View
          style={{
            height: v(Platform.OS === 'web' ? 300 : 180),
          }}
        />
      )}
    </View>
  );
};

export default PaymentsBarChart;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      padding: s(8),
    },
  });
